import { Button, message, Modal, Popconfirm, Switch, Table, Tabs, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { PERM } from "../../../auth/permissions";
import { useMe } from "../../../auth/MeContext";
import axiosInstance from "../../../axios/axios-instance";
import { RoleAuditLog, RoleSummary, RoleUser, SCOPE_LABELS } from "../../../@type/role.types";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import Card from "../../../components/common/Card";
import { pathAPI } from "../../../utils/pathApi";
import RoleFormModal from "./RoleFormModal";

const ROW_BUTTON_MIN_WIDTH = 80;

const errorMessage = (e: unknown): string => {
  const err = e as { message?: string; response?: { data?: { message?: string } } };
  return err?.response?.data?.message || err?.message || "Request failed";
};

const AuditLogTable = () => {
  const [rows, setRows] = useState<RoleAuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(pathAPI.roleAuditLogs, { params: { page, size: 20 } })
      .then((r) => {
        setRows(r.data?.data ?? []);
        setTotal(r.data?.total ?? 0);
      })
      .catch((e) => message.error(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [page]);

  const columns: ColumnsType<RoleAuditLog> = [
    { title: "Time", dataIndex: "created_date", render: (v: string) => moment(v).format("YYYY/MM/DD HH:mm:ss") },
    { title: "Action", dataIndex: "action", render: (v: string) => <Tag>{v}</Tag> },
    { title: "Actor", dataIndex: "actor_admin_id" },
    { title: "Role", dataIndex: "role_id" },
    { title: "Target admin", dataIndex: "target_admin_id" },
    { title: "Before", dataIndex: "before_value", ellipsis: true },
    { title: "After", dataIndex: "after_value", ellipsis: true },
  ];

  return (
    <Table<RoleAuditLog>
      rowKey="id"
      size="small"
      loading={loading}
      columns={columns}
      dataSource={rows}
        pagination={{ current: page + 1, pageSize: 20, total, showSizeChanger: false, showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`, onChange: (p) => setPage(p - 1) }}
    />
  );
};

const RoleManagement = () => {
  const { can } = useMe();
  const canManage = can(PERM.ROLE_MANAGE);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [usersModal, setUsersModal] = useState<{ role: RoleSummary; users: RoleUser[] } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(pathAPI.roles)
      .then((r) => setRoles(r.data?.data ?? []))
      .catch((e) => message.error(errorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (id: number | null) => {
    setEditingId(id);
    setFormOpen(true);
  };

  const toggleActive = (role: RoleSummary, active: boolean) =>
    axiosInstance
      .put(`${pathAPI.roles}/${role.id}/active`, { active })
      .then(load)
      .catch((e) => message.error(errorMessage(e)));

  const remove = (role: RoleSummary) =>
    axiosInstance
      .delete(`${pathAPI.roles}/${role.id}`)
      .then(() => {
        message.success("Deleted");
        load();
      })
      .catch((e) => message.error(errorMessage(e)));

  const showUsers = (role: RoleSummary) =>
    axiosInstance
      .get(`${pathAPI.roles}/${role.id}/users`)
      .then((r) => setUsersModal({ role, users: r.data?.data ?? [] }))
      .catch((e) => message.error(errorMessage(e)));

  const columns: ColumnsType<RoleSummary> = [
    {
      title: "Role",
      dataIndex: "name",
      render: (_: string, r) => (
        <div>
          <b>{r.name}</b> {r.is_system && <Tag color="purple">system</Tag>}
          <div className="text-xs" style={{ color: "var(--app-text-muted)" }}>{r.code}</div>
        </div>
      ),
    },
    {
      title: "Scope",
      dataIndex: "scope_type",
      render: (v: RoleSummary["scope_type"], r) => (
        <span>
          {SCOPE_LABELS[v]}
          {v === "COUNTRY" && (r.multi_country ? " · multi" : " · single")}
        </span>
      ),
    },
    { title: "Permissions", dataIndex: "permission_count", align: "center" },
    {
      title: "Users",
      dataIndex: "user_count",
      align: "center",
      render: (v: number, r) => (
        <Button type="link" onClick={() => showUsers(r)} disabled={v === 0}>
          {v}
        </Button>
      ),
    },
    {
      title: "Active",
      dataIndex: "active",
      align: "center",
      render: (v: boolean, r) => (
        <Switch checked={v} disabled={!canManage || r.code === "SUPER_ADMIN"} onChange={(c) => toggleActive(r, c)} />
      ),
    },
    {
      title: "Actions",
      align: "center",
      render: (_: unknown, r) => (
        <div className="flex gap-2 justify-center">
          <ActionButton sx={{ minWidth: ROW_BUTTON_MIN_WIDTH }} onClick={() => openForm(r.id)}>
            {canManage && r.code !== "SUPER_ADMIN" ? "Edit" : "View"}
          </ActionButton>
          {canManage && !r.is_system && (
            <Popconfirm title="Delete this role?" onConfirm={() => remove(r)} disabled={r.user_count > 0}>
              <span>
                <CancelButton sx={{ minWidth: ROW_BUTTON_MIN_WIDTH }} disabled={r.user_count > 0}>
                  Delete
                </CancelButton>
              </span>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card>
      <Tabs
        items={[
          {
            key: "roles",
            label: "Roles",
            children: (
              <>
                {canManage && (
                  <div className="text-right mb-3">
                    <ActionButton appSize="large" onClick={() => openForm(null)}>
                      Create role
                    </ActionButton>
                  </div>
                )}
                <Table<RoleSummary> rowKey="id" loading={loading} columns={columns} dataSource={roles} pagination={false} />
              </>
            ),
          },
          { key: "audit", label: "Audit log", children: <AuditLogTable /> },
        ]}
      />
      <RoleFormModal
        open={formOpen}
        roleId={editingId}
        allRoles={roles}
        readOnly={!canManage}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          load();
        }}
      />
      <Modal
        open={!!usersModal}
        title={usersModal ? `Users with role: ${usersModal.role.name}` : ""}
        footer={null}
        onCancel={() => setUsersModal(null)}
      >
        {usersModal?.users.map((u) => (
          <div key={u.id} className="py-1 border-b">
            {u.full_name} — {u.email}
          </div>
        ))}
      </Modal>
    </Card>
  );
};

export default RoleManagement;
